import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { AddSongStep } from 'fym-shared/src/music24/catalogAddSong';
import { TypedMemo } from '../../../common/types';
import { isCheckedMark, isCurrentMark, StepMark, STEPPER_STEPS } from './addSongFlowSteps';
import { addSongFlowMessages } from './addSongFlowMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AddSongStepperProps {
  marks: Record<Exclude<AddSongStep, 'done'>, StepMark>;
  /** The label of step 3: "ISRC or link", "ISRC" or "Link" (design b2). */
  codesLabel: 'both' | 'isrc' | 'link';
  /** Absent while the song is not saved: no step but step 1 can open. */
  onOpen?: (step: AddSongStep) => void;
}

const MARK_CLASS: Record<StepMark, string | undefined> = {
  now: styles.stepNow,
  nowDone: styles.stepNow,
  done: styles.stepDone,
  skipped: styles.stepSkipped,
  todo: undefined,
};

/**
 * The stepper of the top bar (design catalog-add-song a·why): a check on a step with data, a dashed
 * circle on a skipped step, the step on screen in the brand colours. A click opens a step. Below
 * 900 px it turns into five bars (design g).
 */
const AddSongStepperComponent: React.FC<AddSongStepperProps> = ({ marks, codesLabel, onOpen }) => {
  const intl = useIntl();
  const labels = useMemo(
    () => ({
      song: intl.formatMessage(addSongFlowMessages.stepSong),
      release: intl.formatMessage(addSongFlowMessages.stepRelease),
      codes: intl.formatMessage(
        {
          both: addSongFlowMessages.stepCodes,
          isrc: addSongFlowMessages.stepCodesIsrc,
          link: addSongFlowMessages.stepCodesLink,
        }[codesLabel]
      ),
      cover: intl.formatMessage(addSongFlowMessages.stepCover),
      audio: intl.formatMessage(addSongFlowMessages.stepAudio),
      lyrics: intl.formatMessage(addSongFlowMessages.stepLyrics),
    }),
    [codesLabel, intl]
  );

  return (
    <ol className={styles.steps}>
      {STEPPER_STEPS.map((step, index) => (
        <li key={step}>
          <button
            type="button"
            className={clsx(styles.step, MARK_CLASS[marks[step]])}
            disabled={onOpen === undefined || isCurrentMark(marks[step])}
            aria-current={isCurrentMark(marks[step]) ? 'step' : undefined}
            onClick={() => onOpen?.(step)}
          >
            <span className={styles.stepDot}>{isCheckedMark(marks[step]) ? '✓' : index + 1}</span>
            {labels[step]}
          </button>
        </li>
      ))}
    </ol>
  );
};

export const AddSongStepper = TypedMemo(AddSongStepperComponent);

interface AddSongBarsProps {
  marks: Record<Exclude<AddSongStep, 'done'>, StepMark>;
}

/** The five-bar stepper of a phone (design g1). */
const AddSongBarsComponent: React.FC<AddSongBarsProps> = ({ marks }) => (
  <div className={styles.bars} aria-hidden>
    {STEPPER_STEPS.map((step) => (
      <i
        key={step}
        className={clsx(isCurrentMark(marks[step]) && styles.barNow, marks[step] === 'done' && styles.barDone)}
      />
    ))}
  </div>
);

export const AddSongBars = TypedMemo(AddSongBarsComponent);
